import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { isEmpty, get, omit } from 'lodash';
import { Spin, Button, message, Modal } from 'antd';
import { useNavigate } from 'react-router-dom';
import MappingPanel from '@components/mapping-panel';
import { DisconnectOutlined, WifiOutlined } from '@ant-design/icons';
import NavigationBar from '@components/navigation-bar';
import ScreenViewer from './ScreenViewer';
import Timeline from './Timeline';
import { getEventAttributes, getEventName } from '../transform';
import { useGetMappedEventsWithOutOfBox } from '@api/emp/queryHooks';
import { useConnectionData } from '@src/pages/VisualActivation/store';
import {
  VisualActivationWrapper,
  HeaderWrapper,
  HeaderContent,
  HeaderTitle,
  TitleText,
  StatusIndicator,
  HeaderActions,
  ContentWrapper,
  ScreenViewerPanel,
  TimelinePanel,
  PanelHeader,
  PanelTitle,
  PanelContent,
  LoadingOverlay,
  EmptyState,
  EmptyStateIcon,
  ClientIdValue,
  StyledDrawer,
} from './styles';
import FloatBtn from './Timeline/FloatBtn';
import { useMappingPanelStore } from '@components/mapping-panel/store';
import { SOCKET_EVENTS } from '@src/pages/VisualActivation/constant';
import { getDeviceInfo } from '../../utils';
import { MappedEvent } from '@src/emp-3.0/types/mapped-event';
import { transformRulesToState } from '@src/components/mapping-panel/utils/handleRulesUtils';
import { MappedEventTypes } from '@src/common/types/empTypes';

const USELESS_KEYS = ['frame'];
const HIDE_KEYS = ['frame', 'Target Url', 'Network Request Response Code'];

const CONNECT_ERROR_MESSAGE = {
  force_disconnect:
    'The room has been disconnected by the host, you can exit the room and continue to work on other rooms',
  app_client_exit:
    'The app client has exited the room, you can exit the room and continue to work on other rooms',
};

const WorkSpace = ({
  roomId,
  sendMessage,
}: {
  roomId: string;
  sendMessage: (message: string, data?: any, skipAck?: boolean) => Promise<void>;
}) => {
  const navigate = useNavigate();
  const [selectEvent, setSelectEvent] = useState<any>(null);
  const [connectError, setConnectError] = useState<{
    type: 'force_disconnect' | 'app_client_exit';
  } | null>(null);
  const { socket, isConnected, roomList, screenshots, rawData, reset } = useConnectionData();

  const {
    mappingPanelOpen,
    setMappingPanelOpen,
    setMappingRules,
    setEventCategory,
    setMappedName,
    setMappedEvent,
  } = useMappingPanelStore((state) => state);

  const { data: allMappedEvents, isLoading: isLoadingAllMappedEvents } =
    useGetMappedEventsWithOutOfBox();

  useEffect(() => {
    const handleAppClientExit = () => {
      setConnectError({ type: 'app_client_exit' });
    };

    socket?.on(SOCKET_EVENTS.APP_CLIENT_EXIT, handleAppClientExit);
    return () => {
      socket?.off(SOCKET_EVENTS.APP_CLIENT_EXIT, handleAppClientExit);
    };
  }, [socket, reset, navigate]);

  const onCreateEvent = useCallback(
    (
      v:
        | { type: 'create'; eventName: string; eventAttributes: Record<string, string> }
        | { type: 'update'; mappedEvent: MappedEvent },
    ) => {
      if (v.type === 'create') {
        const mappingRules = [
          {
            eventName: v.eventName,
            rules: Object.entries(v.eventAttributes)
              .filter(([key]) => !HIDE_KEYS.includes(key))
              .map(([key, value]) => [
                {
                  tagKey: key.toString(),
                  tagValue: {
                    operator: 'eq' as any,
                    value: value.toString(),
                  },
                },
              ]),
          },
        ];
        setMappingRules(mappingRules as any);
        setMappedEvent({
          description: '[created by mobile visual labeling]',
        } as unknown as MappedEventTypes);
        setMappingPanelOpen(true);
      } else {
        setMappedEvent({
          ...v.mappedEvent,
          event_id: v.mappedEvent?.event_id,
          related_metrics: (v.mappedEvent?.related_metrics ?? [])?.map((item) => item.name),
        } as unknown as MappedEventTypes);
        setEventCategory(v.mappedEvent.event_category);
        setMappedName(v.mappedEvent.event_name);
        setMappingRules(transformRulesToState(v.mappedEvent.rules as any));
        setMappingPanelOpen(true);
      }
    },
    [setMappingRules, setMappingPanelOpen, setEventCategory, setMappedName],
  );

  const onDisconnect = async () => {
    try {
      if (connectError?.type !== 'force_disconnect') {
        await sendMessage(SOCKET_EVENTS.LEAVE_ROOM, { room_id: roomId }, false);
      }
      reset();
      navigate('/visual-activation');
    } catch (error: any) {
      message.error(error.message || 'Failed to disconnect');
      if (error.message === 'No room found') {
        reset();
        navigate('/visual-activation');
      }
    }
  };

  const timelineData = useMemo(() => {
    const events: React.ComponentProps<typeof Timeline>['timelineData'] = [];
    rawData.forEach((item) => {
      if (!isEmpty(item.eventData)) {
        const { ue_pr, dtm } = item.eventData;
        try {
          const event = JSON.parse(ue_pr).dt;
          const eventName = getEventName(event);
          const fullEventAttributes = getEventAttributes(eventName, event);
          events.push({
            sessionId: '',
            inSession: 0,
            eventId: item.eventData.eid,
            eventName,
            derivedTstamp: new Date(parseInt(dtm)).toISOString(),
            eventAttributes: omit(fullEventAttributes, USELESS_KEYS),
            fullEventAttributes,
            floatContent: (
              <FloatBtn
                event={{
                  eventName,
                  eventAttributes: fullEventAttributes,
                }}
                onCreateEvent={onCreateEvent}
              />
            ),
            // Removed checksum and timestamp to fix linter errors
          });
        } catch (error) {
          console.error('Error parsing ue_pr:', error);
        }
      }
    });

    return events;
  }, [rawData, onCreateEvent]);

  const room = roomList.rooms?.find((room) => room.room_id === roomId);

  const { deviceName, osVersion } = getDeviceInfo(room?.device_model || '');

  return (
    <VisualActivationWrapper>
      <HeaderWrapper>
        <NavigationBar />
        <HeaderContent>
          <HeaderTitle>
            <TitleText>
              <span>Client ID:</span>
              <ClientIdValue>{room?.clid || 'N/A'}</ClientIdValue>
              <StatusIndicator isConnected={isConnected} />
              <span
                style={{
                  fontSize: '14px',
                  color: isConnected ? '#10b981' : '#ef4444',
                  fontWeight: 500,
                }}
              >
                {isConnected ? 'Connected' : 'Disconnected'}
              </span>
            </TitleText>
          </HeaderTitle>
          <HeaderActions>
            {isConnected && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <WifiOutlined style={{ color: '#10b981' }} />
                <span style={{ color: '#6b7280', fontSize: '14px' }}>
                  {timelineData.length} events tracked
                </span>
              </div>
            )}
            <Button onClick={onDisconnect} type="primary" danger>
              <DisconnectOutlined />
              Disconnect
            </Button>
          </HeaderActions>
        </HeaderContent>
      </HeaderWrapper>

      <ContentWrapper>
        <ScreenViewerPanel>
          <PanelHeader>
            <PanelTitle>
              {deviceName} ({room?.app_id})
            </PanelTitle>
          </PanelHeader>
          <PanelContent>
            {isLoadingAllMappedEvents && (
              <LoadingOverlay>
                <Spin size="large" />
              </LoadingOverlay>
            )}
            {screenshots.length === 0 ? (
              <EmptyState>
                <EmptyStateIcon>📱</EmptyStateIcon>
                <p style={{ margin: 0, fontSize: '16px', color: '#6b7280' }}>
                  No screenshots available
                </p>
                <p style={{ margin: 0, fontSize: '14px', marginTop: '8px', color: '#6b7280' }}>
                  Start interacting with your mobile app to capture screenshots
                </p>
              </EmptyState>
            ) : (
              <ScreenViewer
                onCreateEvent={onCreateEvent}
                selectEvent={selectEvent}
                screenshotData={screenshots}
                allMappedEvents={get(allMappedEvents, 'data')}
              />
            )}
          </PanelContent>
        </ScreenViewerPanel>

        <TimelinePanel>
          <PanelHeader>
            <PanelTitle>Real-time Timeline</PanelTitle>
          </PanelHeader>
          <PanelContent>
            {timelineData.length === 0 ? (
              <EmptyState>
                <EmptyStateIcon>⏱️</EmptyStateIcon>
                <p style={{ margin: 0, fontSize: '16px', color: '#6b7280' }}>
                  No events captured yet
                </p>
                <p style={{ margin: 0, fontSize: '14px', marginTop: '8px', color: '#6b7280' }}>
                  Events will appear here as they are tracked from your mobile app
                </p>
              </EmptyState>
            ) : (
              <Timeline
                selectEvent={selectEvent}
                timelineData={timelineData}
                onClick={setSelectEvent}
              />
            )}
          </PanelContent>
        </TimelinePanel>
      </ContentWrapper>
      <StyledDrawer width={1300} maskClosable={false} open={mappingPanelOpen}>
        <MappingPanel />
      </StyledDrawer>
      <Modal
        title="Room Disconnected"
        open={!!connectError}
        closable={false}
        onOk={onDisconnect}
        okText="Exit"
        okButtonProps={{
          danger: true,
        }}
        cancelButtonProps={{
          style: {
            display: 'none',
          },
        }}
      >
        <p>{CONNECT_ERROR_MESSAGE[connectError?.type || '']}</p>
      </Modal>
    </VisualActivationWrapper>
  );
};

export default WorkSpace;
